import { User } from 'lucide-react';

import { formatDateTime } from '@/utils/format';
import type { DispatchPlaceHistoryItem } from '@/features/dispatch-places/dispatch-places.types';

import { useHistorySection } from './use-history-section';

// ── Type label map ────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  general: 'Actualización general',
  capacity: 'Modificación de capacidad',
  status: 'Cambio de estado',
  address: 'Actualización de dirección',
  contact: 'Actualización de contacto',
  billing: 'Actualización de facturación',
  banking: 'Actualización bancaria',
  provider: 'Cambio de proveedor',
  location: 'Actualización de ubicación',
  schedule: 'Actualización de horario',
  creation: 'Creación del punto',
  update: 'Actualización',
};

function resolveTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

function resolveResponsable(responsable: DispatchPlaceHistoryItem['responsable']): string | null {
  if (!responsable) return null;
  if (responsable.name && responsable.name.trim() !== '') return responsable.name;
  if (responsable.email && responsable.email.trim() !== '') return responsable.email;
  return null;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function HistoryItemSkeleton() {
  return (
    <div className="pb-8 last:pb-0 relative">
      <div className="absolute -left-[31px] top-1 h-3 w-3 rounded-full ring-4 ring-base-100 bg-base-300" />
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="skeleton h-3.5 w-48 rounded" />
          <div className="skeleton h-4 w-28 rounded" />
        </div>
        <div className="skeleton h-3 w-32 rounded" />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface HistorySectionProps {
  dp_id: string;
}

export function HistorySection(props: HistorySectionProps) {
  const { items, isLoading } = useHistorySection(props.dp_id);

  if (!isLoading && items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-base-content/50">
        Sin cambios registrados.
      </p>
    );
  }

  return (
    <div className="ml-2 border-l-2 border-base-300 pl-6 relative">
      {isLoading && (
        <>
          <HistoryItemSkeleton />
          <HistoryItemSkeleton />
          <HistoryItemSkeleton />
        </>
      )}

      {!isLoading && items.map((item, index) => {
        const responsible = resolveResponsable(item.responsable);
        return (
          <div
            key={`${item.type}-${item.changed_at}-${index}`}
            className="pb-8 last:pb-0 relative"
          >
            {/* Timeline dot */}
            <div className="absolute -left-[31px] top-1 h-3 w-3 rounded-full ring-4 ring-base-100 bg-primary" />

            <div className="flex flex-col gap-1.5">
              {/* Title + timestamp */}
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-bold leading-snug">{item.description}</span>
                <span className="shrink-0 whitespace-nowrap rounded bg-base-200 px-1.5 py-0.5 text-[10px] font-medium opacity-50">
                  {formatDateTime(item.changed_at)}
                </span>
              </div>

              {/* Type label */}
              <span className="text-xs text-base-content/50">
                {resolveTypeLabel(item.type)}
              </span>

              {/* Responsible */}
              {responsible && (
                <div className="flex items-center gap-1.5 text-xs text-base-content/60">
                  <User size={10} className="shrink-0" />
                  <span>Por: {responsible}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
